import { useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';
import BadRequestError from 'App/Errors/BadRequestError';
import {
    RECAPTCHA_V3_DEFAULT_ACTION,
    useRecaptchaV3,
} from 'App/Services/Recaptcha/RecaptchaV3Provider';

/**
 * Lead form submit: honeypot, reCAPTCHA v3/v2, createLead / addContactInfo, nextPhase.
 *
 * @param {object} params
 * @param {string|null} params.captchaV2TriggerCode
 * @param {string} [params.reCaptchasiteKey3]
 * @param {object} params.extraData
 * @param {Function} params.setExtraData
 * @param {{ isBot: boolean|null, failedChecks: array, visitorId: string|null }} params.botInfo
 * @param {object} params.leadStatus
 * @param {array|null} params.offers
 * @param {import('App/Services/Sharpstar/SharpstarServiceHandler').default} params.sharpstarService
 * @param {string|undefined} params.leadHash - urlParams.hash
 * @param {string|null} params.tokenV2
 * @param {Function} params.reqHandler
 * @param {Function} params.nextPhase
 * @returns {{ formSubmit: Function, formSubmitState: { isSubmitting: boolean, error: string|null }, tokenV3: string|null }}
 */
export function useGamblingFormSubmit({
    captchaV2TriggerCode,
    reCaptchasiteKey3,
    extraData,
    setExtraData,
    botInfo,
    leadStatus,
    offers,
    sharpstarService,
    leadHash,
    tokenV2,
    reqHandler,
    nextPhase,
}) {
    const { tokenV3, executeV3 } = useRecaptchaV3();
    const [formSubmitState, setFormSubmitState] = useState({
        isSubmitting: false,
        error: null,
    });

    const formSubmit = useCallback(
        async (e, name, email) => {
            e.preventDefault();

            setFormSubmitState({ isSubmitting: true, error: null });

            const form = new FormData(e.target);

            const hp_token = form.get('name_hp');
            const hp_choice = !!form.get('radio_hp');
            const hp_check = !!form.get('checkbox_hp');

            let cpttknV3 = null;
            if (captchaV2TriggerCode) {
                cpttknV3 = null;
            } else if (reCaptchasiteKey3) {
                cpttknV3 = await executeV3(RECAPTCHA_V3_DEFAULT_ACTION);
            } else {
                cpttknV3 = tokenV3;
            }

            const submitExtraData = {
                ...extraData,
                isbt: botInfo.isBot,
                btfldchk: botInfo.failedChecks,
                fngptid: botInfo.visitorId,
            };
            setExtraData(submitExtraData);

            try {
                if (!leadStatus.push_status) {
                    await reqHandler(() =>
                        sharpstarService.createLead({
                            email,
                            name,
                            offerId: offers[0].id,
                            extraData: submitExtraData,
                            cpttknV3,
                            hp_token,
                            hp_choice,
                            hp_check,
                            cpttknV2: tokenV2,
                        })
                    );
                } else {
                    await reqHandler(() =>
                        sharpstarService.addContactInfo({
                            name,
                            email,
                            hash: leadHash,
                            offerId: offers[0].id,
                            extraData: submitExtraData,
                        })
                    );
                }

                setFormSubmitState({ isSubmitting: false, error: null });
                nextPhase();
            } catch (e) {
                setFormSubmitState({ isSubmitting: false, error: e.message });

                if (e.name === 'BadRequestError') {
                    Sentry.captureException(new BadRequestError());
                }
            }
        },
        [
            captchaV2TriggerCode,
            reCaptchasiteKey3,
            executeV3,
            tokenV3,
            extraData,
            setExtraData,
            botInfo.isBot,
            botInfo.failedChecks,
            botInfo.visitorId,
            leadStatus.push_status,
            offers,
            sharpstarService,
            leadHash,
            tokenV2,
            reqHandler,
            nextPhase,
        ]
    );

    return { formSubmit, formSubmitState, tokenV3 };
}
